import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAllCohortUsersViewCreator } from '@app/view-creator/active-participants-view-creator';
import { useGroupViewCreator } from '@app/view-creator';
import { useChatContext } from 'stream-chat-react';
import type { Channel as StreamChannel } from 'stream-chat';
import { NotificationService, CommunicationMode } from '@stores';
import { useAppCookies } from '@app/providers/AppCookieProvider';

export const useTeamChatController = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const [channel, setChannel] = useState<StreamChannel | undefined>(undefined);
  const [groupName, setGroupName] = useState('Your Team Chat');
  const [headerHeight, setHeaderHeight] = useState(0);
  const { data: groupData, isLoading: viewLoading, refetch: refetchGroup } = useGroupViewCreator(Number(groupId));
  const { data } = useAllCohortUsersViewCreator(Number(groupId));
  const allActiveCohortUsers = data?.allActiveCohortUsers || [];
  const cookies = useAppCookies();
  const { client } = useChatContext();
  const navigate = useNavigate();

  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 2000;
  const retryCount = useRef(0);
  const clearRetryState = () => (retryCount.current = 0);

  /**
   * Attempt to fetch group info (if missing) and/or connect to Stream.
   * Retries a few times before giving up and navigating to 404.
   */
  const attemptConnection = useCallback(async () => {
    if (!client) return; // Stream client not ready yet

    // If we don’t have group data yet, trigger another fetch.
    if (!groupData || !groupData.channel) {
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        const delay = RETRY_DELAY_MS * retryCount.current;
        setTimeout(async () => {
          await refetchGroup(); // fetch new data
          attemptConnection(); // 👈 call *after* the delay
        }, delay);
      } else {
        navigate('/404', { replace: true });
      }
      return;
    }

    // If we do have group info, connect to the channel
    try {
      const channel = client.channel('messaging', groupData.channel);
      setChannel(channel);
      setGroupName(groupData.name);
      clearRetryState();
    } catch (err) {
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        const delay = RETRY_DELAY_MS * retryCount.current;
        setTimeout(async () => {
          await refetchGroup();
          attemptConnection();
        }, delay);
      } else {
        navigate('/404', { replace: true });
      }
    }
  }, [client, groupData, navigate, refetchGroup]);

  useEffect(() => {
    if (!viewLoading) {
      attemptConnection();
    }
  }, [viewLoading, attemptConnection]);

  // Reset browser title when entering or reading the chat (to prevent something like (2) (1) in the title)
  useEffect(() => {
    const originalTitle = document.title;

    const resetTitle = () => {
      document.title = groupName + ' Chat' || originalTitle;
    };

    // Reset on mount
    resetTitle();

    // Reset again when tab regains focus
    window.addEventListener('focus', resetTitle);

    return () => {
      window.removeEventListener('focus', resetTitle);
      document.title = originalTitle;
    };
  }, [groupName]);

  useLayoutEffect(() => {
    const headerEl = document.querySelector('header');
    if (!headerEl) return;

    const update = () => setHeaderHeight(headerEl.offsetHeight);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(headerEl);
    return () => ro.disconnect();
  }, []);

  const notifyCohort = useCallback(async () => {
    await new NotificationService().sendNotification({
      groupId: Number(groupId),
      message: 'A new message was sent in your team chat! Click here to read it and respond: https://wheapp.com/',
      groupName,
      title: 'New We Hate Exercise Message',
      userId: cookies.profile.id,
      communicationMode: CommunicationMode.TEAM_CHAT,
    });
  }, [groupId, groupName, cookies.profile.id]);

  return {
    groupId,
    channel,
    groupName,
    headerHeight,
    allActiveCohortUsers,
    notifyCohort,
  };
};
